import { useEffect, useState } from "react";
import { CalendarCheck, CheckCircle2, Clock, Loader2, Phone } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const GREEN = "#2563EB";
const INK_GREEN = "#1E40AF";

const field = "w-full rounded-xl border border-zinc-300 bg-white px-4 py-3 text-[15px] text-zinc-950 placeholder:text-zinc-400 focus:border-zinc-950 focus:outline-none";

/**
 * Ten minutes on the phone, booked off the page. The board shows what's actually open —
 * mostly afternoons — and the slot closes the second somebody takes it.
 */
export default function CallPicker({ tier = "" }) {
    const [d, setD] = useState(null);
    const [day, setDay] = useState(0);
    const [slot, setSlot] = useState("");
    const [form, setForm] = useState({ name: "", shop: "", phone: "", email: "", about: "" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(null);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    useEffect(() => { api.get("/calls/slots").then(({ data }) => setD(data)).catch(() => setD(false)); }, []);

    const book = async (e) => {
        e.preventDefault();
        setErr(""); setBusy(true);
        try {
            const { data } = await api.post("/calls/book", { ...form, slot, tier });
            setDone(data.call);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || "Couldn't book that one");
            api.get("/calls/slots").then(({ data }) => setD(data)).catch(() => { });
        } finally { setBusy(false); }
    };

    if (!d) return (
        <div className="flex justify-center py-10" data-testid="call-picker-loading">
            <Loader2 className="animate-spin text-zinc-400" />
        </div>
    );

    if (done) return (
        <div className="rounded-3xl border-2 border-[#bdf5b4] bg-[#f6fff4] p-8 text-center" data-testid="call-booked">
            <CheckCircle2 size={26} className="mx-auto" style={{ color: INK_GREEN }} />
            <h3 className="mt-3 font-display text-2xl font-black tracking-tight text-zinc-950">
                You're booked — {done.slot_label}.
            </h3>
            <p className="mx-auto mt-2 max-w-lg text-[15px] font-light text-zinc-600">
                We'll call <span className="font-semibold text-zinc-950">{done.phone}</span> at that time and
                set your shop up while you're on the line. A confirmation is on its way to {done.email}.
            </p>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                need to move it? call {d.phone}
            </p>
        </div>
    );

    const days = d.days || [];
    const today = days[day] || { slots: [] };

    return (
        <div data-testid="call-picker">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] font-medium text-zinc-600">
                <span className="inline-flex items-center gap-1.5"><Clock size={13} /> {d.length_label} · {d.hours_label}</span>
                <span className="inline-flex items-center gap-1.5"><Phone size={13} /> we call you</span>
                <span className="text-zinc-500">{d.tz_label}</span>
            </div>

            <div className="mt-5 flex gap-2 overflow-x-auto pb-1" data-testid="call-days">
                {days.map((x, i) => (
                    <button key={x.date} onClick={() => { setDay(i); setSlot(""); }} data-testid={`call-day-${x.date}`}
                        className={`shrink-0 rounded-xl border px-3.5 py-2 text-left transition-colors ${i === day
                            ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white hover:border-zinc-400"}`}>
                        <span className="block text-[13px] font-bold">{x.label}</span>
                        <span className={`block text-[11px] ${i === day ? "text-zinc-400" : "text-zinc-500"}`}>
                            {x.open ? `${x.open} open` : "full"}
                        </span>
                    </button>
                ))}
            </div>

            <div className="mt-4 flex flex-wrap gap-2" data-testid="call-slots">
                {today.slots.length === 0 && (
                    <p className="text-[14px] text-zinc-600" data-testid="call-day-full">
                        That day is booked out — try another one.
                    </p>
                )}
                {today.slots.map((s, i) => (
                    <button key={s.iso} onClick={() => setSlot(s.iso)} data-testid={`call-slot-${i}`}
                        className={`rounded-full border px-3.5 py-2 text-[13px] font-bold tabular-nums transition-colors ${slot === s.iso
                            ? "border-transparent text-white" : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-950 hover:text-zinc-950"}`}
                        style={slot === s.iso ? { background: GREEN } : undefined}>
                        {s.label}
                    </button>
                ))}
            </div>

            {slot && (
                <form onSubmit={book} className="mt-6 grid gap-3 sm:grid-cols-2" data-testid="call-form">
                    <input className={field} placeholder="Your name" required value={form.name}
                        onChange={set("name")} data-testid="call-form-name" />
                    <input className={field} placeholder="Shop name (if you have one)" value={form.shop}
                        onChange={set("shop")} data-testid="call-form-shop" />
                    <input className={field} placeholder="Phone we should call" required value={form.phone}
                        onChange={set("phone")} data-testid="call-form-phone" />
                    <input className={field} type="email" placeholder="Email" required value={form.email}
                        onChange={set("email")} data-testid="call-form-email" />
                    <textarea className={`${field} sm:col-span-2`} rows={2} value={form.about}
                        onChange={set("about")} data-testid="call-form-about"
                        placeholder="One line about your shop — what you print, what you're stuck on" />
                    {err && <p className="text-sm font-semibold text-red-600 sm:col-span-2" data-testid="call-error">{err}</p>}
                    <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
                        <button type="submit" disabled={busy} data-testid="call-book-submit"
                            className="inline-flex items-center gap-2 rounded-full bg-zinc-950 px-7 py-3.5 text-[15px] font-bold text-white transition-transform duration-200 hover:-translate-y-px disabled:opacity-60">
                            {busy ? <Loader2 size={15} className="animate-spin" /> : <CalendarCheck size={15} />}
                            Book this slot
                        </button>
                        <span className="text-[13px] font-light text-zinc-600">
                            No card, no pitch deck — ten minutes and a real setup.
                        </span>
                    </div>
                </form>
            )}
        </div>
    );
}
